import { createFileRoute, useParams, Outlet, Link, redirect } from '@tanstack/react-router';
import { useQuery, useMutation } from '@tanstack/react-query';
import { trpc } from '@/lib/trpc';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useEffect } from 'react';

export const Route = createFileRoute('/(protected)/orgs/$slug')({
  component: OrganizationLayout,
  beforeLoad: async ({ params }) => {
    // Ensure we have a valid organization slug
    if (!params.slug) {
      throw redirect({
        to: '/orgs',
      });
    }
  },
  loader: ({ params }) => {
    return {
      slug: params.slug,
    };
  },
});

function OrganizationLayout() {
  const { slug } = useParams({ from: '/(protected)/orgs/$slug' });
  
  // Fetch organization info by slug
  const { data, isLoading, error } = useQuery(
    trpc.organizations.getOrganizationBySlug.queryOptions({ slug })
  );
  
  // Set active organization mutation
  const setActiveOrganizationMutation = useMutation(
    trpc.organizations.setActiveOrganization.mutationOptions()
  );
  
  // Set this organization as active when we visit this route
  useEffect(() => {
    if (data?.organization) {
      setActiveOrganizationMutation.mutate({
        organizationId: data.organization.id
      });
    }
  }, [data?.organization?.id]);
  
  if (isLoading) {
    return <div>Loading organization...</div>;
  }
  
  if (error) {
    return <div>Error loading organization: {error.message}</div>;
  }
  
  if (!data?.organization) {
    return <div>Organization not found</div>;
  }
  
  const organization = data.organization;
  const initials = organization.name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
  
  return (
    <div className="space-y-6">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/orgs">Organizations</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to={`/orgs/${slug}`}>{organization.name}</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      
      <div className="flex items-center gap-4">
        <Avatar className="h-16 w-16">
          {organization.logo ? (
            <AvatarImage src={organization.logo} alt={organization.name} />
          ) : null}
          <AvatarFallback className="text-xl">{initials}</AvatarFallback>
        </Avatar>
        <div>
          <h1 className="text-3xl font-bold">{organization.name}</h1>
          <p className="text-muted-foreground">Organization ID: {organization.id}</p>
        </div>
        <div className="ml-auto">
          <Button variant="outline" asChild>
            <Link to={`/orgs/${slug}/settings`}>Organization Settings</Link>
          </Button>
        </div>
      </div>

      <Separator />
      
      {/* Navigation within organization */}
      <div className="flex gap-4 mb-8">
        <Button variant="ghost" asChild>
          <Link
            to={`/orgs/${slug}/workspaces`}
            activeProps={{ className: "bg-accent" }}
          >
            Workspaces
          </Link>
        </Button>
        <Button variant="ghost" asChild>
          <Link
            to={`/orgs/${slug}/members`}
            activeProps={{ className: "bg-accent" }}
          >
            Members
          </Link>
        </Button>
        <Button variant="ghost" asChild>
          <Link
            to={`/orgs/${slug}/invitations`}
            activeProps={{ className: "bg-accent" }}
          >
            Invitations
          </Link>
        </Button>
        <Button variant="ghost" asChild>
          <Link
            to={`/orgs/${slug}/billing`}
            activeProps={{ className: "bg-accent" }}
          >
            Billing
          </Link>
        </Button>
      </div>
      
      <Outlet />
    </div>
  );
} 